import { useState, useEffect, useCallback, useRef } from 'react';
import { CustomButton } from '../types';
import socketService from '../services/socketService';
import { fetchButtons } from '../services/api';
import { useCounters } from '../contexts/CounterContext';
import { useNavigate } from 'react-router-dom';

/**
 * Custom hook for real-time counter data subscription via WebSockets
 * 
 * This hook has been refactored to focus solely on real-time data updates
 * and counter deletion events. Click handling is now exclusively handled by useClickBatcher.
 * 
 * @param counterId - ID of the counter to subscribe to
 * @param isOwnerUrl - Whether the ID is an owner URL rather than a regular counter ID
 * @returns An object containing data and methods for the counter subscription
 */
export function useWebSocketCounter(counterId: string, isOwnerUrl = false) {
  const [buttons, setButtons] = useState<CustomButton[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const isInitializedRef = useRef(false);
  const isDeletedRef = useRef(false); // Track if the counter has been deleted
  const { counters, findCounterByOwnerUrl, loading: countersLoading, refreshCounters } = useCounters();
  const navigate = useNavigate();
  
  // Get the actual counter ID from the URL parameter, regardless of owner/public link
  const actualCounterId = useCallback(() => {
    if (!counterId) return '';
    
    // If counters are still loading, don't try to resolve yet
    if (countersLoading) return '';
    
    // If this is an owner URL, find the real counter ID
    if (isOwnerUrl) {
      const counter = findCounterByOwnerUrl(counterId);
      return counter?._id || '';
    }
    
    // Otherwise use the ID directly
    return counterId;
  }, [counterId, isOwnerUrl, findCounterByOwnerUrl, countersLoading]);

  /**
   * Fetch the initial counter data from the API
   */
  const fetchInitialData = useCallback(async () => {
    if (!counterId || isDeletedRef.current || countersLoading) return; // Skip if counter was deleted or counters still loading

    try {
      // Don't update state if component is unmounting or counter is deleted
      setLoading(true);
      const data = await fetchButtons(counterId, isOwnerUrl);
      
      // Check again if deleted before updating state
      if (!isDeletedRef.current) {
        setButtons(data);
        setLastUpdate(new Date());
        setError(null);
      }
    } catch (err) {
      if (!isDeletedRef.current) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch counter data';
        setError(errorMessage);
      }
    } finally {
      if (!isDeletedRef.current) {
        setLoading(false);
      }
    }
  }, [counterId, isOwnerUrl, countersLoading]);

  /**
   * Handle counter updates received via WebSocket
   */
  const handleCounterUpdate = useCallback((data: any) => {
    if (isDeletedRef.current) return; // Skip if counter was deleted
    
    const counterIdToMatch = actualCounterId();
    if (!counterIdToMatch) return; // Skip if no valid counter ID yet
    
    // Check if the data is in object format with counterId and buttons properties
    // or directly an array of buttons (old format)
    const isObjectFormat = data && typeof data === 'object' && 'counterId' in data && Array.isArray(data.buttons);
    const receivedCounterId = isObjectFormat ? data.counterId : counterId;
    const buttonsData = isObjectFormat ? data.buttons : data;
    
    if (receivedCounterId === counterIdToMatch) {
      // Ensure buttonsData is actually an array before updating state
      if (Array.isArray(buttonsData)) {
        // Update the buttons state with the server state (source of truth)
        setButtons(buttonsData);
        setLastUpdate(new Date());
      }
    }
  }, [actualCounterId, counterId]);

  /**
   * Handle counter settings updates received via WebSocket
   */
  const handleCounterSettingsUpdate = useCallback((data: any) => {
    if (isDeletedRef.current) return; // Skip if counter was deleted
    
    const counterIdToMatch = actualCounterId();
    if (!counterIdToMatch) return; // Skip if no valid counter ID yet
    
    // Check if the data is in the expected format
    if (data && typeof data === 'object' && 'counterId' in data && 'counter' in data) {
      const receivedCounterId = data.counterId;
      
      if (receivedCounterId === counterIdToMatch) {
        console.log('Received counter settings update:', data.counter);
        
        // Refresh counters to get the updated settings
        // This will update the CounterContext with the latest settings
        refreshCounters();
        
        // Mark that we got an update
        setLastUpdate(new Date());
      }
    }
  }, [actualCounterId, refreshCounters]);

  /**
   * Handle counter deletion event from WebSocket
   */
  const handleCounterDeleted = useCallback((data: { counterId: string, message: string }) => {
    const counterIdToMatch = actualCounterId();
    if (!counterIdToMatch) return; // Skip if no valid counter ID yet
    
    if (data.counterId === counterIdToMatch) {
      // Mark the counter as deleted to prevent further API calls
      isDeletedRef.current = true;
      
      // Different redirect based on access type:
      // - Owner URL: Show the "counter deleted" confirmation screen
      // - Public URL: Show the "counter not found" screen
      if (isOwnerUrl) {
        navigate('/counter-deleted');
      } else {
        navigate('/counter-not-found');
      }
    }
  }, [actualCounterId, navigate, isOwnerUrl]);

  /**
   * Manually refresh the counter data
   */
  const refreshData = useCallback(async () => {
    if (isDeletedRef.current || countersLoading) return; // Skip if counter was deleted or counters are loading
    
    try {
      await fetchInitialData();
    } catch (err) {
      // Silent fail in production
    }
  }, [fetchInitialData, countersLoading]);

  // Set up WebSocket connection and subscription
  useEffect(() => {
    // Use the actual counter ID for subscriptions
    const counterIdToUse = actualCounterId();
    if (!counterIdToUse || isDeletedRef.current) return; // Skip if counter was deleted or no valid ID
    
    // Initialize WebSocket connection
    const socket = socketService.connect();
    
    // Register event handlers
    socketService.on('counterUpdate', handleCounterUpdate);
    socketService.on('counterSettingsUpdate', handleCounterSettingsUpdate);
    socketService.on('counterDeleted', handleCounterDeleted);
    
    // Subscribe to the counter updates
    socketService.subscribeToCounter(counterIdToUse);
    
    // Load initial data if not already loaded
    if (!isInitializedRef.current && !isDeletedRef.current) {
      fetchInitialData();
      isInitializedRef.current = true;
    }
    
    // Cleanup on unmount
    return () => {
      socketService.off('counterUpdate', handleCounterUpdate);
      socketService.off('counterSettingsUpdate', handleCounterSettingsUpdate);
      socketService.off('counterDeleted', handleCounterDeleted);
      socketService.unsubscribeFromCounter(counterIdToUse);
    };
  }, [counterId, isOwnerUrl, fetchInitialData, handleCounterUpdate, handleCounterSettingsUpdate, handleCounterDeleted, actualCounterId]);

  return {
    buttons,
    loading: loading || countersLoading,
    error,
    lastUpdate,
    refreshData
  };
} 